import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, TrendingUp, MessageSquare, X, ThumbsUp, ThumbsDown, Minus, MapPin, Calendar, Filter } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { KPICard } from '../components/ui/KPICard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// ALDI Nord regions
const regions = [{
  id: 'konzern',
  name: 'Gesamtkonzern',
  icon: Building2
}, {
  id: 'nord',
  name: 'ALDI Nord Region Nord'
}, {
  id: 'ost',
  name: 'ALDI Nord Region Ost'
}, {
  id: 'west',
  name: 'ALDI Nord Region West'
}, {
  id: 'mitte',
  name: 'ALDI Nord Region Mitte'
}];
const regionData: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
  avgRating: number;
  trend: number;
}> = {
  konzern: {
    positive: 8450,
    neutral: 2830,
    negative: 1420,
    total: 12700,
    avgRating: 4.1,
    trend: 0.12
  },
  nord: {
    positive: 2120,
    neutral: 680,
    negative: 310,
    total: 3110,
    avgRating: 4.3,
    trend: 0.18
  },
  ost: {
    positive: 2400,
    neutral: 820,
    negative: 380,
    total: 3600,
    avgRating: 4.1,
    trend: 0.1
  },
  west: {
    positive: 2350,
    neutral: 890,
    negative: 460,
    total: 3700,
    avgRating: 3.9,
    trend: -0.05
  },
  mitte: {
    positive: 1580,
    neutral: 440,
    negative: 270,
    total: 2290,
    avgRating: 4.0,
    trend: 0.08
  }
};
const topicClusters: Record<string, Array<{
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  comments: Array<{
    text: string;
    market: string;
    date: string;
    rating: number;
  }>;
}>> = {
  konzern: [{
    name: 'Preis-Leistung',
    size: 3800,
    sentiment: 'positive',
    comments: [{
      text: 'Super Preise wie immer, hier kaufe ich gerne ein!',
      market: 'ALDI Nord Hamburg',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Beste Preise in der Gegend, Qualität stimmt auch.',
      market: 'ALDI Nord Berlin-Mitte',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Preis-Leistung ist unschlagbar.',
      market: 'ALDI Nord Köln',
      date: '2024-01-26',
      rating: 5
    }]
  }, {
    name: 'Schnelle Kassen',
    size: 2600,
    sentiment: 'positive',
    comments: [{
      text: 'Die Kassierer sind super schnell!',
      market: 'ALDI Nord Düsseldorf',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Nie lange Wartezeiten an der Kasse.',
      market: 'ALDI Nord Frankfurt',
      date: '2024-01-27',
      rating: 4
    }]
  }, {
    name: 'Wartezeiten',
    size: 2200,
    sentiment: 'negative',
    comments: [{
      text: 'Samstags viel zu voll, lange Schlangen.',
      market: 'ALDI Nord Berlin-Kreuzberg',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Zu wenig Kassen offen am Wochenende.',
      market: 'ALDI Nord Dortmund',
      date: '2024-01-27',
      rating: 2
    }]
  }, {
    name: 'Sauberkeit',
    size: 1800,
    sentiment: 'positive',
    comments: [{
      text: 'Immer sauber und ordentlich.',
      market: 'ALDI Nord Hannover',
      date: '2024-01-28',
      rating: 5
    }]
  }, {
    name: 'Auswahl',
    size: 1600,
    sentiment: 'neutral',
    comments: [{
      text: 'Sortiment ist okay, aber manchmal fehlen Sachen.',
      market: 'ALDI Nord Leipzig',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Für einen Discounter gute Auswahl.',
      market: 'ALDI Nord Dresden',
      date: '2024-01-27',
      rating: 4
    }]
  }, {
    name: 'Backstation',
    size: 1400,
    sentiment: 'positive',
    comments: [{
      text: 'Die Backwaren sind immer frisch und lecker!',
      market: 'ALDI Nord Münster',
      date: '2024-01-28',
      rating: 5
    }]
  }, {
    name: 'Parkplatz',
    size: 1200,
    sentiment: 'negative',
    comments: [{
      text: 'Parkplatzsituation ist oft schwierig.',
      market: 'ALDI Nord Köln-Ehrenfeld',
      date: '2024-01-28',
      rating: 2
    }]
  }, {
    name: 'Öffnungszeiten',
    size: 900,
    sentiment: 'neutral',
    comments: [{
      text: 'Schade dass sonntags geschlossen ist.',
      market: 'ALDI Nord Essen',
      date: '2024-01-28',
      rating: 3
    }]
  }],
  nord: [{
    name: 'Preis-Leistung',
    size: 620,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Schnelle Kassen',
    size: 480,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Backstation',
    size: 380,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Wartezeiten',
    size: 280,
    sentiment: 'negative',
    comments: []
  }, {
    name: 'Sauberkeit',
    size: 350,
    sentiment: 'positive',
    comments: []
  }]
};
const monthlyTrendData: Record<string, Array<{
  month: string;
  rating: number;
  reviews: number;
}>> = {
  konzern: [{
    month: 'Feb 23',
    rating: 3.98,
    reviews: 9800
  }, {
    month: 'Mär 23',
    rating: 4.0,
    reviews: 10200
  }, {
    month: 'Apr 23',
    rating: 3.99,
    reviews: 10000
  }, {
    month: 'Mai 23',
    rating: 4.02,
    reviews: 10500
  }, {
    month: 'Jun 23',
    rating: 4.04,
    reviews: 11000
  }, {
    month: 'Jul 23',
    rating: 4.02,
    reviews: 10800
  }, {
    month: 'Aug 23',
    rating: 4.05,
    reviews: 11200
  }, {
    month: 'Sep 23',
    rating: 4.07,
    reviews: 11500
  }, {
    month: 'Okt 23',
    rating: 4.06,
    reviews: 12000
  }, {
    month: 'Nov 23',
    rating: 4.08,
    reviews: 12300
  }, {
    month: 'Dez 23',
    rating: 4.09,
    reviews: 12500
  }, {
    month: 'Jan 24',
    rating: 4.1,
    reviews: 12700
  }],
  nord: [{
    month: 'Feb 23',
    rating: 4.12,
    reviews: 2400
  }, {
    month: 'Mär 23',
    rating: 4.15,
    reviews: 2500
  }, {
    month: 'Apr 23',
    rating: 4.18,
    reviews: 2450
  }, {
    month: 'Mai 23',
    rating: 4.2,
    reviews: 2600
  }, {
    month: 'Jun 23',
    rating: 4.22,
    reviews: 2700
  }, {
    month: 'Jul 23',
    rating: 4.24,
    reviews: 2650
  }, {
    month: 'Aug 23',
    rating: 4.26,
    reviews: 2800
  }, {
    month: 'Sep 23',
    rating: 4.25,
    reviews: 2900
  }, {
    month: 'Okt 23',
    rating: 4.27,
    reviews: 2950
  }, {
    month: 'Nov 23',
    rating: 4.28,
    reviews: 3000
  }, {
    month: 'Dez 23',
    rating: 4.29,
    reviews: 3050
  }, {
    month: 'Jan 24',
    rating: 4.3,
    reviews: 3110
  }]
};
const SimpleTreemap = ({
  data,
  onClick
}: {
  data: Array<{
    name: string;
    size: number;
    sentiment: string;
  }>;
  onClick: (item: any) => void;
}) => {
  const total = data.reduce((sum, item) => sum + item.size, 0);
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#fff3cd',
          border: '#FFD301',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-2 h-full" data-id="element-733">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      let colSpan = 1,
        rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} onClick={() => onClick(item)} className="rounded-lg p-3 flex flex-col justify-center items-center text-center cursor-pointer transition-opacity hover:opacity-80" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-734">
            <span className="font-semibold text-sm leading-tight" style={{
          color: colors.text
        }} data-id="element-735">
              {item.name}
            </span>
            <span className="text-xs mt-1 opacity-70" style={{
          color: colors.text
        }} data-id="element-736">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
};
export function KonzernStimmungPage() {
  const [selectedRegion, setSelectedRegion] = useState('konzern');
  const [selectedTopic, setSelectedTopic] = useState<(typeof topicClusters)['konzern'][0] | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const currentData = regionData[selectedRegion] || regionData.konzern;
  const currentTopics = topicClusters[selectedRegion] || topicClusters.konzern;
  const currentTrend = monthlyTrendData[selectedRegion] || monthlyTrendData.konzern;
  const totalSentiments = currentData.positive + currentData.neutral + currentData.negative;
  const positivePercent = Math.round(currentData.positive / totalSentiments * 100);
  const neutralPercent = Math.round(currentData.neutral / totalSentiments * 100);
  const negativePercent = Math.round(currentData.negative / totalSentiments * 100);
  const handleTopicClick = (topic: any) => {
    const fullTopic = currentTopics.find(t => t.name === topic.name);
    if (fullTopic && fullTopic.comments.length > 0) {
      setSelectedTopic(fullTopic);
      setIsDialogOpen(true);
    }
  };
  const treemapData = currentTopics.map(topic => ({
    name: topic.name,
    size: topic.size,
    sentiment: topic.sentiment
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-737">
      {/* Region Filter */}
      <Card className="!p-4" data-id="element-738">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-739">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-740">
            <Filter size={16} data-id="element-741" />
            <span data-id="element-742">Region:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-743">
            {regions.map(region => {
            const isSelected = selectedRegion === region.id;
            const Icon = region.icon;
            return <button key={region.id} onClick={() => setSelectedRegion(region.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-aldi-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-744">
                  {Icon && <Icon size={14} data-id="element-745" />}
                  {region.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-746">
        <KPICard title="Durchschnittliche Bewertung" value={currentData.avgRating.toFixed(1)} change={currentData.trend > 0 ? `+${currentData.trend.toFixed(2)}` : currentData.trend.toFixed(2)} trend={currentData.trend >= 0 ? 'up' : 'down'} icon={<TrendingUp size={18} data-id="element-748" />} subtext="vs. Vormonat" data-id="element-747" />
        <KPICard title="Gesamtbewertungen" value={currentData.total.toLocaleString('de-DE')} change="+6%" trend="up" icon={<MessageSquare size={18} data-id="element-750" />} subtext="diese Periode" data-id="element-749" />
        <KPICard title="Positive Resonanz" value={`${positivePercent}%`} change={`${currentData.positive.toLocaleString('de-DE')}`} trend="up" icon={<ThumbsUp size={18} data-id="element-752" />} subtext="Kommentare" data-id="element-751" />
        <KPICard title="Negative Resonanz" value={`${negativePercent}%`} change={`${currentData.negative.toLocaleString('de-DE')}`} trend="down" icon={<ThumbsDown size={18} data-id="element-754" />} subtext="Kommentare" data-id="element-753" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-755">
        {/* Absolute Resonanz */}
        <Card data-id="element-756">
          <div className="p-6" data-id="element-757">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-758">
              Absolute Resonanz
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-759">
              Verteilung der Sentiment-Analyse
            </p>

            <div className="mb-6" data-id="element-760">
              <div className="h-12 rounded-lg overflow-hidden flex" data-id="element-761">
                <div style={{
                width: `${positivePercent}%`,
                backgroundColor: '#639754'
              }} className="flex items-center justify-center" data-id="element-762">
                  {positivePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-763">
                      {positivePercent}%
                    </span>}
                </div>
                <div style={{
                width: `${neutralPercent}%`,
                backgroundColor: '#FFD301'
              }} className="flex items-center justify-center" data-id="element-764">
                  {neutralPercent > 10 && <span className="text-gray-800 text-sm font-semibold" data-id="element-765">
                      {neutralPercent}%
                    </span>}
                </div>
                <div style={{
                width: `${negativePercent}%`,
                backgroundColor: '#E03C32'
              }} className="flex items-center justify-center" data-id="element-766">
                  {negativePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-767">
                      {negativePercent}%
                    </span>}
                </div>
              </div>
            </div>

            <div className="space-y-3" data-id="element-768">
              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-769">
                <div className="flex items-center gap-3" data-id="element-770">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#639754'
                }} data-id="element-771">
                    <ThumbsUp size={18} className="text-white" data-id="element-772" />
                  </div>
                  <div data-id="element-773">
                    <div className="font-semibold text-gray-900" data-id="element-774">Positiv</div>
                    <div className="text-xs text-gray-500" data-id="element-775">
                      Zufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-776">
                  <div className="text-2xl font-bold" style={{
                  color: '#639754'
                }} data-id="element-777">
                    {currentData.positive.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-778">
                    {positivePercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fff9db'
            }} data-id="element-779">
                <div className="flex items-center gap-3" data-id="element-780">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#FFD301'
                }} data-id="element-781">
                    <Minus size={18} className="text-gray-800" data-id="element-782" />
                  </div>
                  <div data-id="element-783">
                    <div className="font-semibold text-gray-900" data-id="element-784">Neutral</div>
                    <div className="text-xs text-gray-500" data-id="element-785">
                      Gemischte Meinungen
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-786">
                  <div className="text-2xl font-bold" style={{
                  color: '#b39500'
                }} data-id="element-787">
                    {currentData.neutral.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-788">
                    {neutralPercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-789">
                <div className="flex items-center gap-3" data-id="element-790">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-791">
                    <ThumbsDown size={18} className="text-white" data-id="element-792" />
                  </div>
                  <div data-id="element-793">
                    <div className="font-semibold text-gray-900" data-id="element-794">Negativ</div>
                    <div className="text-xs text-gray-500" data-id="element-795">
                      Unzufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-796">
                  <div className="text-2xl font-bold" style={{
                  color: '#E03C32'
                }} data-id="element-797">
                    {currentData.negative.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-798">
                    {negativePercent}% aller Bewertungen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Themen Cluster Treemap */}
        <Card data-id="element-799">
          <div className="p-6" data-id="element-800">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-801">
              Themen-Cluster
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-802">
              Häufig erwähnte Themen in Bewertungen (klicken für Details)
            </p>
            <div className="h-80" data-id="element-803">
              <SimpleTreemap data={treemapData} onClick={handleTopicClick} data-id="element-804" />
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100" data-id="element-805">
              <div className="flex items-center gap-2" data-id="element-806">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#639754'
              }} data-id="element-807"></div>
                <span className="text-xs text-gray-600" data-id="element-808">Positiv</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-809">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#FFD301'
              }} data-id="element-810"></div>
                <span className="text-xs text-gray-600" data-id="element-811">Neutral</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-812">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-813"></div>
                <span className="text-xs text-gray-600" data-id="element-814">Negativ</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 12-Month Trend Chart */}
      <Card data-id="element-815">
        <div className="p-6" data-id="element-816">
          <div className="flex items-center justify-between mb-6" data-id="element-817">
            <div data-id="element-818">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-819">
                Reputationsentwicklung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-820">
                Durchschnittliche Bewertung der letzten 12 Monate
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-821">
              <div className="flex items-center gap-2" data-id="element-822">
                <div className="w-3 h-3 rounded-full bg-aldi-blue" data-id="element-823"></div>
                <span className="text-gray-600" data-id="element-824">Ø Bewertung</span>
              </div>
            </div>
          </div>

          <div className="h-72" data-id="element-825">
            <ResponsiveContainer width="100%" height="100%" data-id="element-826">
              <LineChart data={currentTrend} data-id="element-827">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-828" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-829" />
                <YAxis domain={[3.8, 4.5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => value.toFixed(1)} data-id="element-830" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number) => [value.toFixed(2), 'Ø Bewertung']} data-id="element-831" />
                <Line type="monotone" dataKey="rating" stroke="#00005f" strokeWidth={3} dot={{
                fill: '#00005f',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#009ee3',
                stroke: '#00005f',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-832" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between" data-id="element-833">
            <div className="flex items-center gap-4" data-id="element-834">
              <div className="flex items-center gap-2" data-id="element-835">
                <TrendingUp size={18} style={{
                color: '#639754'
              }} data-id="element-836" />
                <span className="text-sm text-gray-600" data-id="element-837">
                  <span className="font-semibold" style={{
                  color: '#639754'
                }} data-id="element-838">
                    +
                    {((currentTrend[11].rating - currentTrend[0].rating) * 100 / currentTrend[0].rating).toFixed(1)}
                    %
                  </span>{' '}
                  Verbesserung im Jahresvergleich
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-500" data-id="element-839">
              Aktuell:{' '}
              <span className="font-semibold text-gray-900" data-id="element-840">
                {currentTrend[11].rating.toFixed(2)}
              </span>{' '}
              Sterne
            </div>
          </div>
        </div>
      </Card>

      {/* Topic Detail Dialog */}
      <AnimatePresence data-id="element-841">
        {isDialogOpen && selectedTopic && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-[600] p-4" onClick={() => setIsDialogOpen(false)} data-id="element-842">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-843">
              <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-844">
                <div className="flex items-center justify-between" data-id="element-845">
                  <div data-id="element-846">
                    <div className="flex items-center gap-3" data-id="element-847">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-848">
                        {selectedTopic.name}
                      </h3>
                      <Badge variant={selectedTopic.sentiment === 'positive' ? 'success' : selectedTopic.sentiment === 'negative' ? 'danger' : 'warning'} data-id="element-849">
                        {selectedTopic.sentiment === 'positive' ? 'Positiv' : selectedTopic.sentiment === 'negative' ? 'Negativ' : 'Neutral'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-850">
                      {selectedTopic.size.toLocaleString('de-DE')} Erwähnungen
                      in Kundenbewertungen
                    </p>
                  </div>
                  <button onClick={() => setIsDialogOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors" data-id="element-851">
                    <X size={20} data-id="element-852" />
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]" data-id="element-853">
                <div className="space-y-4" data-id="element-854">
                  {selectedTopic.comments.map((comment, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-855">
                      <div className="flex items-start justify-between gap-4 mb-2" data-id="element-856">
                        <div className="flex items-center gap-2" data-id="element-857">
                          <MapPin size={14} className="text-aldi-blue" data-id="element-858" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-859">
                            {comment.market}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-860">
                          <div className="flex" data-id="element-861">
                            {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < comment.rating ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-862">
                                ★
                              </span>)}
                          </div>
                          <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-863">
                            <Calendar size={12} data-id="element-864" />
                            {new Date(comment.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 italic" data-id="element-865">
                        "{comment.text}"
                      </p>
                    </motion.div>)}
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-866">
                <Button variant="primary" onClick={() => setIsDialogOpen(false)} data-id="element-867">
                  Schließen
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}